import { createSignal, For, Show } from 'solid-js';
import Text from './Text';
import SellbotPausedBadge from './SellbotPausedBadge';
import QuantityInput from './QuantityInput';
import { showConfirm } from './Dialog';
import { useToast } from './MessageBox';
import { useApi, isOk } from '../api';
import { useTranslation } from '../hooks/useTranslation';
import type { SellbotLimitDto, SellbotLimitRequest } from '../api';
import styles from '../pages/Sellbot.module.css';
import { firstError } from '../api/envelope';
import { formatQuantity } from '../services/quantityFormatter';

const SCOPES = ['Global', 'PerCustomer'] as const;
const PERIODS = ['Hour', 'Day', 'Week', 'Month', 'RollingCustom'] as const;
const BASES = ['Rate', 'Stock'] as const;

interface SellbotLimitsEditorProps {
    botId: string;
    offerId: string;
    stackSize?: number;
    kind: 'Ankauf' | 'Verkauf';
    limits: SellbotLimitDto[];
    onChanged: () => void | Promise<void>;
}

export default function SellbotLimitsEditor(props: SellbotLimitsEditorProps) {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [basis, setBasis] = createSignal<string>('Rate');
    const [scope, setScope] = createSignal<string>('Global');
    const [period, setPeriod] = createSignal<string>('Day');
    const [rollingHours, setRollingHours] = createSignal<number | ''>(24);
    const [maxCount, setMaxCount] = createSignal<number | ''>(0);

    const canStock = () => props.kind === 'Ankauf';

    const addLimit = async () => {
        const maxCountVal = Number(maxCount() || 0);
        if (maxCountVal <= 0) { toast?.error(t('SELLBOT_TOAST_LIMIT_MAX_REQUIRED')); return; }
        const isStock = canStock() && basis() === 'Stock';
        const body: SellbotLimitRequest = {
            kind: props.kind, basis: isStock ? 'Stock' : 'Rate',
            scope: isStock ? 'Global' : scope(), period: isStock ? 'Day' : period(),
            rollingHours: !isStock && period() === 'RollingCustom' ? Math.max(1, Number(rollingHours() || 1)) : 0,
            maxCount: maxCountVal, isEnabled: true,
        };
        const res = await api.sellbot.createLimit(props.botId, props.offerId, body);
        if (isOk(res.data)) { setMaxCount(0); await props.onChanged(); }
        else toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_UPDATE_FAILED'));
    };

    const toggleLimit = async (limit: SellbotLimitDto) => {
        const body: SellbotLimitRequest = {
            kind: limit.kind, basis: limit.basis, scope: limit.scope, period: limit.period,
            rollingHours: limit.rollingHours, maxCount: limit.maxCount, isEnabled: !limit.isEnabled,
        };
        const res = await api.sellbot.updateLimit(props.botId, limit.id, body);
        if (isOk(res.data)) await props.onChanged();
        else toast?.error(t('SELLBOT_TOAST_UPDATE_FAILED'));
    };

    const deleteLimit = async (limitId: string) => {
        if (!await showConfirm(t('SELLBOT_CONFIRM_DELETE_LIMIT'), true, { confirmLabel: t('COMMON_DELETE'), cancelLabel: t('COMMON_CANCEL') })) return;
        const res = await api.sellbot.deleteLimit(props.botId, limitId);
        if (isOk(res.data)) await props.onChanged();
        else toast?.error(t('SELLBOT_TOAST_DELETE_FAILED'));
    };

    const titleKey = props.kind === 'Ankauf' ? 'SELLBOT_LIMITS_TITLE_ANKAUF' : 'SELLBOT_LIMITS_TITLE_VERKAUF';

    return (
        <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
            <div class={styles.sectionHeader}><h2><Text val={titleKey} /></h2><span class={styles.helper}><Text val="SELLBOT_LIMITS_HELP" /></span></div>
            <div class={styles.tierList} style={{ 'padding-left': '0' }}>
                <Show when={props.limits.length === 0}>
                    <div class={styles.tierEmpty}><i class="fa-solid fa-gauge-high" /> <Text val="SELLBOT_NO_LIMITS" /></div>
                </Show>
                <For each={props.limits}>{(limit) => (
                    <div class={`${styles.tierRow} ${!limit.isEnabled ? styles.paused : ''}`}>
                        <Show when={limit.basis === 'Stock'} fallback={<>
                            <span class={`${styles.badge} ${styles.badgeInfo}`}>{t('SELLBOT_LIMIT_SCOPE_' + limit.scope.toUpperCase())}</span>
                            <span class={styles.tierCount}>
                                {limit.period === 'RollingCustom'
                                    ? t('SELLBOT_LIMIT_PERIOD_ROLLINGCUSTOM_VALUE', limit.rollingHours)
                                    : t('SELLBOT_LIMIT_PERIOD_' + limit.period.toUpperCase())}
                            </span>
                            <span class={styles.helper}>{t('SELLBOT_LIMIT_CONSUMED', formatQuantity(limit.consumedInWindow, props.stackSize), formatQuantity(limit.maxCount, props.stackSize))}</span>
                        </>}>
                            <span class={`${styles.badge} ${styles.badgeInfo}`}><Text val="SELLBOT_LIMIT_BASIS_STOCK" /></span>
                            <span class={styles.helper}>{t('SELLBOT_LIMIT_STOCK_CURRENT', formatQuantity(limit.consumedInWindow, props.stackSize), formatQuantity(limit.maxCount, props.stackSize))}</span>
                        </Show>
                        <Show when={!limit.isEnabled}><SellbotPausedBadge /></Show>
                        <span style={{ flex: 1 }} />
                        <button type="button" class="btn btn-sm btn-secondary" onClick={() => void toggleLimit(limit)}>{t(limit.isEnabled ? 'SELLBOT_PAUSE' : 'SELLBOT_RESUME')}</button>
                        <button type="button" class="btn btn-sm btn-danger" aria-label={t('COMMON_DELETE')} title={t('COMMON_DELETE')} onClick={() => void deleteLimit(limit.id)}><i class="fa-solid fa-trash" /></button>
                    </div>
                )}</For>
                <Show when={canStock()}>
                    <div class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                        <select value={basis()} onChange={(e) => setBasis(e.currentTarget.value)}>
                            <For each={BASES}>{(b) => <option value={b}>{t('SELLBOT_LIMIT_BASIS_' + b.toUpperCase())}</option>}</For>
                        </select>
                        <span class={styles.helper}><Text val="SELLBOT_LIMIT_BASIS_HELP" /></span>
                    </div>
                </Show>
                <form class={styles.tierAddRow} onSubmit={(e) => { e.preventDefault(); void addLimit(); }}>
                    <Show when={!canStock() || basis() === 'Rate'}>
                        <select value={scope()} onChange={(e) => setScope(e.currentTarget.value)}>
                            <For each={SCOPES}>{(s) => <option value={s}>{t('SELLBOT_LIMIT_SCOPE_' + s.toUpperCase())}</option>}</For>
                        </select>
                        <select value={period()} onChange={(e) => setPeriod(e.currentTarget.value)}>
                            <For each={PERIODS}>{(p) => <option value={p}>{t('SELLBOT_LIMIT_PERIOD_' + p.toUpperCase())}</option>}</For>
                        </select>
                        <Show when={period() === 'RollingCustom'}>
                            <input type="number" min="1" placeholder={t('SELLBOT_LIMIT_ROLLING_HOURS')} value={rollingHours()} onInput={(e) => setRollingHours(e.currentTarget.value === '' ? '' : Number(e.currentTarget.value))} />
                        </Show>
                    </Show>
                    <QuantityInput class={styles.countInput} value={Number(maxCount() || 0)} onChange={setMaxCount} stackSize={props.stackSize} presets={false} placeholder={t(canStock() && basis() === 'Stock' ? 'SELLBOT_LIMIT_MAX_STOCK' : 'SELLBOT_LIMIT_MAX_COUNT')} />
                    <button type="submit" class="btn btn-sm btn-secondary"><i class="fa-solid fa-plus" /> <Text val="SELLBOT_ADD_LIMIT" /></button>
                </form>
            </div>
        </div>
    );
}
